/**
 * Sports Bet Slip Component
 * Unified component for bet slip (works for both desktop and mobile)
 * Shows BACK/LAY bets with liability warnings for LAY bets
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useBetting } from '@/Context/BettingContext';
import { useExposure } from '@/hooks/useExposure';
import { useInvalidateSports } from '@/hooks/useSports';
import { useScreenSize } from '@/hooks/useScreenSize';
import { BET_TYPES } from '@/utils/constants';
import { getUID } from '@/utils';
import storage from '@/utils/storage';
import { X, ChevronDown, Zap, Trash2, Settings, Share2 } from 'lucide-react';
import styles from './SportsBetSlip.module.css';

const SportsBetSlip = ({ isOpen, onClose }) => {
  const { selectedBets, removeBet, updateBetStake, placeBets } = useBetting();
  const { balance: exposureBalance } = useExposure();
  const { isMobile } = useScreenSize();
  const [quickBetEnabled, setQuickBetEnabled] = useState(false);
  const [selectedQuickAmount, setSelectedQuickAmount] = useState(100);
  const [betType, setBetType] = useState('Single');
  const [showLayWarning, setShowLayWarning] = useState({});
  const queryClient = useQueryClient();
  const invalidateSports = useInvalidateSports();
  const uid = getUID();

  const quickBetAmounts = [50, 100, 500, 1000];

  // Get balance from wallet (preferred) or fallback to exposure balance
  const getWalletBalance = useCallback(() => {
    // Try to get credit from wallet storage (supports multiple coins)
    const currentCoin = storage.getKey('coin') || 'BTC';
    const credits = storage.getKey('credits') || {};
    const coinKey = currentCoin.toLowerCase();
    
    if (credits && typeof credits === 'object' && credits[coinKey] !== undefined) {
      const creditValue = credits[coinKey];
      // Convert to number if needed
      const numValue = typeof creditValue === 'number' ? creditValue : parseFloat(creditValue) || 0;
      return numValue;
    }
    
    // Fallback to single credit value
    const credit = storage.getKey('credit');
    if (credit !== null && credit !== undefined) {
      const numValue = typeof credit === 'number' ? credit : parseFloat(credit) || 0;
      return numValue;
    }
    
    // Final fallback to exposure balance
    return exposureBalance || 0;
  }, [exposureBalance]);

  // State for wallet balance (updates reactively)
  const [walletBalance, setWalletBalance] = useState(() => getWalletBalance());

  // Listen for credit updates from wallet
  useEffect(() => {
    const handleCreditUpdate = () => {
      setWalletBalance(getWalletBalance());
    };

    // Listen to credit_update events from wallet
    window.addEventListener('credit_update', handleCreditUpdate);
    
    // Also check storage periodically for updates
    const interval = setInterval(() => {
      setWalletBalance(getWalletBalance());
    }, 1000);

    return () => {
      window.removeEventListener('credit_update', handleCreditUpdate);
      clearInterval(interval);
    };
  }, [getWalletBalance]);

  // Use wallet balance (preferred) or fallback to exposure balance
  const balance = walletBalance || exposureBalance || 0;

  // Handle MAX button click - set stake to available balance
  const handleMaxClick = (betId) => {
    updateBetStake(betId, balance);
  };

  // Use React Query mutation for placing bets
  const placeBetsMutation = useMutation({
    mutationFn: placeBets,
    onSuccess: (result) => {
      if (uid) {
        invalidateSports.invalidateExposures(uid);
        invalidateSports.invalidateWallet(uid);
      }
      queryClient.invalidateQueries({ queryKey: ['userExposures'] });
      queryClient.invalidateQueries({ queryKey: ['walletBalance'] });
      if (result.errors && result.errors.length === 0 && isMobile && onClose) {
        onClose();
      }
    },
  });

  // Calculate liability for LAY bets
  const calculateLiability = (stake, odds) => {
    return stake * (odds - 1);
  };

  // Calculate potential profit for BACK bets
  const calculateProfit = (stake, odds) => {
    return stake * (odds - 1);
  };

  const handleStakeChange = (betId, value) => {
    const numValue = parseFloat(value) || 0;
    updateBetStake(betId, numValue);
  };

  const handleQuickAmountClick = (amount) => {
    setSelectedQuickAmount(amount);
    selectedBets.forEach((bet) => {
      updateBetStake(bet.id, amount);
    });
  };

  const handlePlaceBets = async () => {
    const layBets = selectedBets.filter((b) => b.type === BET_TYPES.LAY && b.stake > 0);
    const unconfirmedLays = layBets.filter((b) => !showLayWarning[b.id]);

    if (unconfirmedLays.length > 0) {
      setShowLayWarning((prev) => ({
        ...prev,
        [unconfirmedLays[0].id]: true,
      }));
      return;
    }

    const validBets = selectedBets.filter((b) => b.stake > 0);

    if (validBets.length === 0) {
      alert('Please enter stake amounts');
      return;
    }

    placeBetsMutation.mutate(validBets, {
      onSuccess: (result) => {
        if (result.errors && result.errors.length > 0) {
          alert(`Some bets failed: ${result.errors.map((e) => e.message).join(', ')}`);
        } else {
          alert('Bets placed successfully!');
          setShowLayWarning({});
        }
      },
      onError: (error) => {
        alert(`Error placing bets: ${error.message}`);
      },
    });
  };

  const totalLiability = selectedBets
    .filter((b) => b.type === BET_TYPES.LAY && b.stake > 0)
    .reduce((sum, b) => sum + calculateLiability(b.stake, b.odds), 0);

  const totalStake = selectedBets
    .filter((b) => b.stake > 0)
    .reduce((sum, b) => sum + b.stake, 0);

  const totalPotentialWin = selectedBets
    .filter((b) => b.stake > 0)
    .reduce((sum, b) => {
      if (b.type === BET_TYPES.BACK) {
        return sum + calculateProfit(b.stake, b.odds) + b.stake;
      }
      return sum;
    }, 0);

  // Desktop view - always visible sidebar
  if (!isMobile) {
    if (selectedBets.length === 0) {
      return (
        <div className={styles.betSlip}>
          <div className={styles.header}>
            <h3>Sports Bet Slip</h3>
          </div>
          <div className={styles.empty}>
            <p>No bets selected</p>
            <p className={styles.emptyHint}>Click on prices in the market to add bets</p>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.betSlip}>
        <div className={styles.header}>
          <h3>Sports Bet Slip</h3>
          <span className={styles.betCount}>{selectedBets.length}</span>
        </div>

        <div className={styles.betsList}>
          {selectedBets.map((bet) => {
            const isLay = bet.type === BET_TYPES.LAY;
            const liability = isLay ? calculateLiability(bet.stake || 0, bet.odds) : 0;
            const profit = !isLay ? calculateProfit(bet.stake || 0, bet.odds) : 0;
            const needsWarning = isLay && bet.stake > 0 && !showLayWarning[bet.id];

            return (
              <div
                key={bet.id}
                className={`${styles.betItem} ${isLay ? styles.layBet : styles.backBet}`}
              >
                <div className={styles.betHeader}>
                  <span className={styles.betType}>{isLay ? 'LAY' : 'BACK'}</span>
                  <button
                    className={styles.removeBtn}
                    onClick={() => removeBet(bet.id)}
                    aria-label="Remove bet"
                  >
                    ×
                  </button>
                </div>

                <div className={styles.betDetails}>
                  <div className={styles.selection}>{bet.selectionName}</div>
                  <div className={styles.match}>{bet.matchTitle}</div>
                  <div className={styles.odds}>
                    Odds: <span className={styles.oddsValue}>{bet.odds}</span>
                  </div>
                </div>

                {needsWarning && (
                  <div className={styles.layWarning}>
                    <strong>⚠️ LAY Bet Warning</strong>
                    <p>You are betting AGAINST this outcome.</p>
                    <p>Your liability will be: {liability.toFixed(2)}</p>
                    <button
                      className={styles.confirmBtn}
                      onClick={() =>
                        setShowLayWarning((prev) => ({ ...prev, [bet.id]: true }))
                      }
                    >
                      I Understand
                    </button>
                  </div>
                )}

                <div className={styles.stakeInput}>
                  <label>Stake:</label>
                  <input
                    type="number"
                    value={bet.stake || ''}
                    onChange={(e) => handleStakeChange(bet.id, e.target.value)}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                </div>

                {bet.stake > 0 && (
                  <div className={styles.calculation}>
                    {isLay ? (
                      <>
                        <div className={styles.liability}>
                          <span>Liability:</span>
                          <span className={styles.liabilityAmount}>
                            {liability.toFixed(2)}
                          </span>
                        </div>
                        <div className={styles.liabilityNote}>
                          You risk {liability.toFixed(2)} if this outcome wins
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={styles.profit}>
                          <span>Potential Profit:</span>
                          <span className={styles.profitAmount}>{profit.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className={styles.footer}>
          <div className={styles.summary}>
            <div className={styles.summaryRow}>
              <span>Total Stake:</span>
              <span>{totalStake.toFixed(2)}</span>
            </div>
            {totalLiability > 0 && (
              <div className={styles.summaryRow}>
                <span>Total Liability:</span>
                <span className={styles.liabilityTotal}>{totalLiability.toFixed(2)}</span>
              </div>
            )}
            <div className={styles.summaryRow}>
              <span>Available Balance:</span>
              <span>{balance.toFixed(2)}</span>
            </div>
          </div>

          <button
            className={styles.placeBetBtn}
            onClick={handlePlaceBets}
            disabled={placeBetsMutation.isPending || totalStake === 0}
          >
            {placeBetsMutation.isPending ? 'Placing...' : 'Place Bets'}
          </button>
        </div>
      </div>
    );
  }

  // Mobile view - bottom modal
  if (isMobile && !isOpen) return null;

  return (
    <>
      <div className={styles.overlay} onClick={onClose} />
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <button className={styles.menuBtn} onClick={onClose} aria-label="Close betslip">
              <X size={20} />
            </button>
            <div className={styles.headerTitle}>
              <span className={styles.betslipTitle}>Betslip</span>
              {selectedBets.length > 0 && (
                <span className={styles.betCount}>{selectedBets.length}</span>
              )}
            </div>
            <button className={styles.collapseBtn} onClick={onClose} aria-label="Collapse betslip">
              <ChevronDown size={20} />
            </button>
          </div>
          <div className={styles.headerRight}>
            <span className={styles.quickBetLabel}>QUICK BET</span>
            <button
              className={`${styles.quickBetToggle} ${quickBetEnabled ? styles.active : ''}`}
              onClick={() => setQuickBetEnabled(!quickBetEnabled)}
            >
              <div className={styles.toggleSlider} />
            </button>
            {quickBetEnabled && <Zap size={16} className={styles.zapIcon} />}
          </div>
        </div>

        {quickBetEnabled && (
          <div className={styles.quickBetInfo}>
            <p>
              QuickBet mode is on! After single click on any selection, it will place your bet immediately.
            </p>
          </div>
        )}

        {/* <div className={styles.betTypeTabs}>
          <button
            className={`${styles.tab} ${betType === 'Single' ? styles.active : ''}`}
            onClick={() => setBetType('Single')}
          >
            Single
          </button>
          <button
            className={`${styles.tab} ${betType === 'Combo' ? styles.active : ''}`}
            onClick={() => setBetType('Combo')}
          >
            Combo
          </button>
          <button
            className={`${styles.tab} ${betType === 'System' ? styles.active : ''}`}
            onClick={() => setBetType('System')}
          >
            System
          </button>
        </div> */}

        <div className={styles.betsList}>
          {selectedBets.length === 0 ? (
            <div className={styles.empty}>
              <p>No bets selected</p>
              <p className={styles.emptyHint}>Click on prices in the market to add bets</p>
            </div>
          ) : (
            selectedBets.map((bet) => {
              const isLay = bet.type === BET_TYPES.LAY;
              const liability = isLay ? calculateLiability(bet.stake || 0, bet.odds) : 0;
              const profit = !isLay ? calculateProfit(bet.stake || 0, bet.odds) : 0;
              const needsWarning = isLay && bet.stake > 0 && !showLayWarning[bet.id];

              return (
                <div
                  key={bet.id}
                  className={`${styles.betItem} ${isLay ? styles.layBet : styles.backBet}`}
                >
                  <div className={styles.betHeader}>
                    <button
                      className={styles.removeBtn}
                      onClick={() => removeBet(bet.id)}
                      aria-label="Remove bet"
                    >
                      <X size={18} />
                    </button>
                    <div className={styles.betTypeBadge}>
                      <span className={styles.sportIcon}>⚽</span>
                      <span className={styles.betTypeText}>{bet.marketName || 'Match Odds'}</span>
                    </div>
                  </div>

                  <div className={styles.betDetails}>
                    <div className={styles.matchTitle}>{bet.matchTitle}</div>
                    <div className={styles.selection}>{bet.selectionName}</div>
                    <div className={styles.oddsRow}>
                      <span className={styles.oddsLabel}>Odds:</span>
                      <span className={`${styles.oddsValue} ${isLay ? styles.layOdds : styles.backOdds}`}>
                        {bet.odds}
                        {isLay ? ' ↓' : ' ↑'}
                      </span>
                    </div>
                  </div>

                  {needsWarning && (
                    <div className={styles.layWarning}>
                      <strong>⚠️ LAY Bet Warning</strong>
                      <p>You are betting AGAINST this outcome.</p>
                      <p>Your liability will be: {liability.toFixed(2)}</p>
                      <button
                        className={styles.confirmBtn}
                        onClick={() =>
                          setShowLayWarning((prev) => ({ ...prev, [bet.id]: true }))
                        }
                      >
                        I Understand
                      </button>
                    </div>
                  )}

                  <div className={styles.stakeInput}>
                    <label>Stake:</label>
                    <div className={styles.stakeInputGroup}>
                      <input
                        type="number"
                        value={bet.stake || ''}
                        onChange={(e) => handleStakeChange(bet.id, e.target.value)}
                        placeholder="0"
                        min="0"
                        step="1"
                      />
                      <button 
                        className={styles.maxBtn} 
                        onClick={() => handleMaxClick(bet.id)}
                        type="button"
                        aria-label="Set maximum stake"
                      >
                        MAX
                      </button>
                    </div>
                  </div>

                  {bet.stake > 0 && (
                    <div className={styles.calculation}>
                      {isLay ? (
                        <div className={styles.liability}>
                          <span>Liability:</span>
                          <span className={styles.liabilityAmount}>{liability.toFixed(2)}</span>
                        </div>
                      ) : (
                        <div className={styles.profit}>
                          <span>Potential Win:</span>
                          <span className={styles.profitAmount}>{(profit + bet.stake).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {selectedBets.length > 0 && (
          <div className={styles.quickAmounts}>
            {quickBetAmounts.map((amount) => (
              <button
                key={amount}
                className={`${styles.quickAmountBtn} ${
                  selectedQuickAmount === amount ? styles.active : ''
                }`}
                onClick={() => handleQuickAmountClick(amount)}
              >
                {amount}
              </button>
            ))}
            <div className={styles.selectedAmount}>
              <span className={styles.checkmark}>✓</span>
              <span>{selectedQuickAmount}</span>
            </div>
          </div>
        )}

        {selectedBets.length > 0 && (
          <div className={styles.summary}>
            <div className={styles.summaryRow}>
              <span>Total Bet:</span>
              <span>{totalStake.toFixed(2)}</span>
            </div>
            {totalPotentialWin > 0 && (
              <div className={styles.summaryRow}>
                <span>POTENTIAL WIN:</span>
                <span className={styles.potentialWin}>{totalPotentialWin.toFixed(2)}</span>
              </div>
            )}
            {totalLiability > 0 && (
              <div className={styles.summaryRow}>
                <span>Total Liability:</span>
                <span className={styles.liabilityTotal}>{totalLiability.toFixed(2)}</span>
              </div>
            )}
          </div>
        )}

        <div className={styles.actions}>
          <button
            className={styles.placeBetBtn}
            onClick={handlePlaceBets}
            disabled={placeBetsMutation.isPending || totalStake === 0}
          >
            {placeBetsMutation.isPending ? 'Placing...' : 'PLACE BET'}
          </button>
          <button className={styles.shareBtn}>
            <Share2 size={18} />
            SHARE
          </button>
        </div>

        <div className={styles.utilities}>
          <button className={styles.utilityBtn} onClick={() => selectedBets.forEach(b => removeBet(b.id))}>
            <Trash2 size={18} />
            Clear
          </button>
          <button className={styles.utilityBtn}>
            <Settings size={18} />
            Odds Settings
            <span className={styles.settingsDot}>•</span>
          </button>
        </div>

        {quickBetEnabled && (
          <div className={styles.quickBetStatus}>
            <Zap size={14} />
            <span>Quick Bet is active with {selectedQuickAmount} bet</span>
          </div>
        )}
      </div>
    </>
  );
};

export default SportsBetSlip;
